<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: $t('messages.evaluate_from_tags'),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherGroup in meta.links.otherGroups"
                                :href="otherGroup.url"
                                :key="otherGroup.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherGroup.text }}
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <EmptyState v-if="subjects.length === 0" picture />
        <div v-else class="flex flex-wrap gap-x-2">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
            </AppButton>
        </div>

        <NavLinks :links="meta.navLinks.modes" class="mt-3" />

        <form action="" @submit.prevent="filter">
            <AppWell v-if="tags.blocks.length" class="mt-3">
                <PageSectionCollapsible class="mt-5">
                    <template v-slot:title> {{ $tc('messages.tags', 2) }} ({{ form.tag_ids.length }}) </template>
                    <QuestionTagSelectorByBlock v-model="form.tag_ids" :blocks="tags.blocks" />

                    <template v-slot:closed>
                        <div class="mt-3">
                            <AppCard>
                                <div class="p-2 flex flex-wrap gap-2">
                                    <BadgePill color="indigo" v-for="tag in selectedTags" :key="tag.id">
                                        {{ tag.name }}
                                    </BadgePill>
                                </div>
                            </AppCard>
                        </div>
                    </template>
                </PageSectionCollapsible>

                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date_from')" for="date_from" />
                            <FormInput
                                id="date_from"
                                v-model="form.date_from"
                                type="datetime-local"
                                name="date_from"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.date_from" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date_to')" for="date_to" />
                            <FormInput
                                id="date_to"
                                v-model="form.date_to"
                                type="datetime-local"
                                name="date_to"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.date_to" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-8">
                        <div class="flex justify-end mt-5">
                            <SubmitFormButton :button-text="$t('messages.filter')" :processing="form.processing" />
                        </div>
                    </div>
                </div>
            </AppWell>

            <div v-if="table.students.length > 0" class="mt-5">
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.students', 1) }}
                                        </th>
                                        <th
                                            v-for="tag in filteredTags"
                                            :key="tag.id"
                                            class="max-w-xs px-1 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            <div :title="tag.name">
                                                {{ tag.initials }}
                                            </div>
                                            <div class="flex justify-center">
                                                <FormInput
                                                    v-model.number="tag.weight"
                                                    type="number"
                                                    step="1"
                                                    class="w-16"
                                                />
                                            </div>
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-4 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            <div class="w-20 flex justify-center">
                                                <IconPlusMinus class="w-6 h-6 text-gray-400" />
                                            </div>
                                        </th>
                                        <th
                                            class="max-w-xs px-1 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.score') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(student, index) in scoredStudents"
                                        :key="student.id"
                                    >
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ student.full_name }}
                                        </td>
                                        <td
                                            v-for="tag in student.tags"
                                            :key="tag.id"
                                            class="max-w-xs px-1 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            :style="`background-color: ${tag.color}`"
                                        >
                                            <span class="tabular-nums">
                                                <BadgePill
                                                    color="gray"
                                                    :title="
                                                        $t('messages.tag_score_reached_and_expected', {
                                                            reached: tag.score,
                                                            expected: tag.expected_level,
                                                        })
                                                    "
                                                >
                                                    {{ tag.score }} / {{ tag.expected_level }}
                                                </BadgePill>
                                            </span>
                                        </td>
                                        <td
                                            class="max-w-sm truncate px-6 py-4 text-center whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <div>
                                                <FormInput
                                                    v-model.number="scoreAdjustments[student.id]"
                                                    type="number"
                                                    max="10"
                                                    min="-10"
                                                    class="w-20"
                                                    step="0.01"
                                                />
                                            </div>
                                        </td>
                                        <td
                                            class="max-w-sm truncate px-6 py-4 text-center whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <div class="w-12">
                                                <span class="tabular-nums">{{ student.score }}</span>
                                            </div>
                                        </td>
                                        <td
                                            class="max-w-sm truncate px-6 py-4 text-center whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            <BadgePill :color="student.pill.color">
                                                {{ student.pill.letters }}
                                            </BadgePill>
                                        </td>
                                    </tr>
                                </tbody>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(tags, index) in table.tag_counters"
                                        :key="index"
                                    >
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ index }}
                                        </td>
                                        <td
                                            v-for="tag in tags"
                                            :key="tag.id"
                                            class="max-w-xs px-1 py-1 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            :style="`background-color: ${tag.color}`"
                                        >
                                            <span class="tabular-nums" :class="{ 'text-gray-300': tag === 0 }">
                                                {{ tag }}
                                            </span>
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <div class="mt-5" v-if="table.students.length">
            <form action="" @submit.prevent="submit">
                <div class="md:grid md:grid-cols-6 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model="saveForm.title"
                                name="title"
                                type="text"
                                class="mt-1"
                            />
                            <FormError :value="saveForm.errors.title" class="mt-2" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-end mt-5">
                    <SubmitFormButton
                        :button-text="$t('messages.save')"
                        :processing="form.processing"
                        :desired-action="!!table.students.length"
                    />
                </div>
            </form>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import { IconCircleCheckFilled, IconPlusMinus } from '@tabler/icons-vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import NavLinks from '@/components/NavLinks.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import BadgePill from '@/components/BadgePill.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import { clamp } from 'lodash';
import GradeTranslator from '@/classes/GradeTranslator.js';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import deburr from 'lodash/deburr';
import PageSectionCollapsible from '../../../../../../Shared/PageSectionCollapsible.vue';
import QuestionTagSelectorByBlock from '../../../../../../components/QuestionTagSelectorByBlock.vue';

export default {
    components: {
        QuestionTagSelectorByBlock,
        PageSectionCollapsible,
        FormLabel,
        FormError,
        FormInput,
        SubmitFormButton,
        BadgePill,
        AppCard,
        AppWell,
        NavLinks,
        ActivityMenuOptions,
        AppButton,
        PanelPage,
        EmptyState,
        IconCircleCheckFilled,
        IconPlusMinus,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'table', 'adjustments', 'filters', 'group', 'tags', 'ranges', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                mode: this.filters.mode,
                tag_ids: this.filters.tag_ids.map((id) => +id),
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
            }),
            filteredTags: this.tags.selected_tags,
            scoreAdjustments: this.adjustments,
            block_search: '',
            gradeTranslator: new GradeTranslator(this.ranges),
            saveForm: this.$inertia.form({
                title: '',
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        submit() {
            this.$inertia.post(this.meta.links.store, {
                title: this.saveForm.title,
                subject_id: this.filters.subject_id,
                students: this.scoredStudents.map((s) => {
                    return {
                        id: s.id,
                        decimal_score: s.score,
                    };
                }),
            });
        },
    },
    computed: {
        scoredStudents: function () {
            const denominator = this.filteredTags.map((t) => t.weight).reduce((a, b) => a + b, 0);
            return this.table.students.map((s) => {
                const scoresMap = new Map(s.tags.map((t) => [t.id, t.score]));
                const expectedMap = new Map(s.tags.map((t) => [t.id, t.expected_level]));
                let weightedScores = this.filteredTags.map((t) => {
                    const max = expectedMap.get(t.id);

                    const tagScore = clamp(scoresMap.get(t.id), 0, max);

                    return (tagScore / max) * t.weight;
                });
                const weightedScore = weightedScores.reduce((a, b) => a + b, 0);
                const scoreAdjustment = this.scoreAdjustments[s.id];
                const decimalScore = clamp((weightedScore / denominator) * 10 + scoreAdjustment, 0, 10);
                const score = decimalScore.toFixed(2);
                const pill = this.gradeTranslator.scoreToPillWithLetters(decimalScore);
                return { ...s, score, pill, weightedScores };
            });
        },
        filteredBlocks: function () {
            const search = deburr(this.block_search.toLowerCase());

            if (search === '') {
                return this.tags.blocks;
            }
            return this.tags.blocks
                .map((b) => {
                    return {
                        ...b,
                        tags: b.tags.filter(
                            (t) =>
                                deburr(t.name.toLowerCase()).includes(search) ||
                                deburr(this.form.tag_ids).includes(t.id),
                        ),
                    };
                })
                .filter((b) => b.tags.length > 0);
        },
        selectedTags: function () {
            return this.tags.blocks.flatMap((b) => b.tags).filter((t) => this.form.tag_ids.includes(t.id));
        },
    },
};
</script>
